'use client';

import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Callout,
  ExpandableCallout,
  PhoneInput,
  TextInput,
} from '@galapagos/design-system/web';
import { FormActions } from '../components/FormActions';
import { PageTitle } from '../components/PageTitle';
import { PrivacyNote } from '../components/PrivacyNote';
import { PrivateFields } from '../components/PrivateFields';
import { SignupShell } from '../components/SignupShell';
import { ensureSignupStepStyle } from '../components/signup-step-style';
import { useSignupApi } from '../hooks/useSignupApi';
import { useLegacySignupParams } from '../hooks/useLegacySignupParams';
import { usePlanDetailsSponsor } from '../hooks/usePlanDetailsSponsor';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { signupSponsorQuery } from '../hooks/useSignupSponsor';
import { signupProgramOptionsQuery } from '../hooks/useSignupProgramOptions';
import { useStepHeading } from '../hooks/useStepHeading';
import {
  buildProfileWrite,
  contactToAsk,
  isValidEmail,
  toE164UsPhone,
} from '../profile/profile-answers';
import { showsPlanDetails } from '../health-plan/plan-details';
import { earliestMissingAnswerStep } from '../resume/earlier-answers';
import { programFloorOutcome } from '../services/program-options';
import {
  ssoSignedInWith,
  useSignupLegalName,
  useSignupSignInContact,
} from '../profile/signup-identity';
import { gateStopStep } from '../minor/gate-stop';
import { readWithFallback } from '../services/read-diagnostics';
import { trackRegFlowMemberRegistered } from '../services/regflow-analytics';
import { useSignupFlowStore } from '../stores/signup-flow-store';
import type { SamlContact } from '@galapagos/shared';
import { readSamlSignedInContact } from '@/services/saml-session';
import { firstSignupStep } from '../steps';
import { EarlierAnswersGate } from './EarlierAnswersGate';

function ContactStep() {
  const { t } = useTranslation('signup');
  const api = useSignupApi();
  const { goToStep, goToNextStep, path } = useSignupNavigation();
  const queryClient = useQueryClient();
  const scenario = useSearchParams().get('scenario');
  const { flow } = useLegacySignupParams();
  const planSponsor = usePlanDetailsSponsor('contact');
  const legalName = useSignupLegalName();
  // An employer SSO member signed in at a roster contact the browser never
  // saw. The flow store forgets it on a reload, so the tab's copy of which
  // kind it was is read back on the SSO path.
  const ssoContact = useSignupFlowStore((state) => state.ssoContact);
  const [rememberedSso, setRememberedSso] = useState<SamlContact | null>(null);
  useEffect(() => {
    if (path === 'sso') setRememberedSso(readSamlSignedInContact());
  }, [path]);
  // Only on the SSO path: a member who left it signed in with a typed contact.
  const ssoKind = path === 'sso' ? (ssoContact?.contact ?? rememberedSso) : null;
  const typedSignIn = useSignupSignInContact();
  const signInContact = ssoKind ? ssoSignedInWith(ssoKind) : typedSignIn;
  const address = useSignupFlowStore((state) => state.profileAddress);
  const sexAssignedAtBirth = useSignupFlowStore((state) => state.sexAssignedAtBirth);
  const planDetails = useSignupFlowStore((state) => state.planDetails);
  const genderIdentity = useSignupFlowStore((state) => state.genderIdentity);
  const pronouns = useSignupFlowStore((state) => state.pronouns);
  const asking = contactToAsk(signInContact);
  const [entered, setEntered] = useState('');
  const [fieldError, setFieldError] = useState(false);
  const [save, setSave] = useState<'idle' | 'saving' | 'failed'>('idle');
  // A ref, not state: a second click in the same frame must see the save that
  // is already running, before React re-renders the button as Loading.
  const savingRef = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const title = asking === 'email' ? t('contact.emailTitle') : t('contact.phoneTitle');
  const headingRef = useStepHeading(title);
  ensureSignupStepStyle();

  const onEdit = (value: string) => {
    setEntered(value);
    setFieldError(false);
  };

  const onContinue = () => {
    if (savingRef.current) return;
    const valid = asking === 'email' ? isValidEmail(entered) : toE164UsPhone(entered) !== null;
    setFieldError(!valid);
    if (!valid) {
      // One error at a time: the field's own message replaces an old save error.
      setSave('idle');
      return;
    }
    const write = buildProfileWrite({
      legalName,
      signInContact,
      enteredContact: entered,
      address,
      sexAssignedAtBirth,
      planDetails,
      genderIdentity,
      pronouns,
    });
    if (!write) {
      // An earlier answer went missing (the gate checked them before this
      // step showed): send the member to the step that collects it rather
      // than reporting a save that never happened.
      const store = useSignupFlowStore.getState();
      const back = earliestMissingAnswerStep('contact', path, {
        programSelection: store.programSelection,
        legalName,
        signInContact,
        birthDate: store.birthDate,
        sexAssignedAtBirth,
        profileAddress: address,
      });
      goToStep(back ?? firstSignupStep(path));
      return;
    }
    savingRef.current = true;
    setSave('saving');
    // A save that finishes after the member left this step must not touch the
    // screen they are on now, and one that finishes after the flow started
    // over (a new flow, a session end) must not move them on. While this
    // screen is still showing, it always leaves the loading state.
    const epoch = useSignupFlowStore.getState().flowEpoch;
    const saveProfile = () => {
      api
        .saveRegistrationProfile(write)
        .then(async (view) => {
          // Only the save that completed registration is the conversion, the
          // same one that publishes member.profile.completed; a re-save is not.
          // It counts even when the member has already left this screen.
          if (view.firstCompletion === true) trackRegFlowMemberRegistered();
          // The options are read when the answer arrives, not at the click: a
          // list still loading then would read as missing. A failed read leaves
          // them missing, which asks the member to choose again.
          const options =
            view.ineligibleProgramKeys.length === 0
              ? []
              : await queryClient.ensureQueryData(signupProgramOptionsQuery(api, scenario)).then(
                  (data) => data.options,
                  () => [],
                );
          return { view, options };
        })
        .then(
          ({ view, options }) => {
            savingRef.current = false;
            if (!mounted.current) return;
            // The flow started over while saving: this flow's answers are gone,
            // so stay put and let the member continue from here.
            if (useSignupFlowStore.getState().flowEpoch !== epoch) {
              setSave('idle');
              return;
            }
            // The server's registration gate comes first: it ends the flow for a
            // member below every program's minimum age, or a minor the sponsor
            // does not cover. The client never looks at the birth date itself.
            const stop = gateStopStep(view.registrationGate);
            if (stop !== null) {
              goToStep(stop);
              return;
            }
            const store = useSignupFlowStore.getState();
            const outcome = programFloorOutcome(
              options,
              store.programSelection ?? [],
              view.ineligibleProgramKeys,
            );
            if (outcome.kind === 'repick') {
              store.setProgramRepickExcluded(view.ineligibleProgramKeys);
              goToStep('substance');
              return;
            }
            // A later save that needs no second choice clears an earlier one's.
            store.setProgramRepickExcluded(null);
            if (outcome.kind === 'trimmed') store.setProgramSelection(outcome.selection);
            goToNextStep('contact');
          },
          () => {
            savingRef.current = false;
            if (mounted.current) setSave('failed');
          },
        );
    };
    if (planSponsor === null) {
      saveProfile();
      return;
    }
    // Plan details are never stored, so a reload since the plan details step
    // lost them: a sponsor that asks for them gets them before the save, which
    // is the last chance to send them. This read decides afresh, even when an
    // earlier one failed and skipped the step; if it fails too, the save goes
    // without them.
    void readWithFallback('sponsor_before_save', 'save_without_plan_details', () =>
      queryClient.fetchQuery(
        signupSponsorQuery(api, scenario, planSponsor.clientId, planSponsor.subClientId),
      ),
    )
      .then((sponsor) => showsPlanDetails({ healthPlan: sponsor?.features.healthPlan, flow }))
      .then((owed) => {
        // A member who left this step, or a flow that started over, while the
        // sponsor answered gets no save and no move.
        const current = mounted.current && useSignupFlowStore.getState().flowEpoch === epoch;
        if (current && !owed) {
          saveProfile();
          return;
        }
        savingRef.current = false;
        if (!mounted.current) return;
        setSave('idle');
        if (current) goToStep('plan-details');
      });
  };

  const status =
    save === 'saving' ? t('contact.saving') : save === 'failed' ? t('contact.saveError') : '';

  return (
    <SignupShell>
      <div className="signup-step">
        <PageTitle title={title} headingRef={headingRef} />
        {asking === 'email' ? (
          <ExpandableCallout
            title={t('contact.emailInfoTitle')}
            actionId="signup.contact-screen-why.toggle"
            data-testid="signup-contact-why"
          >
            {t('contact.emailInfoBody')}
          </ExpandableCallout>
        ) : (
          <ExpandableCallout
            title={t('contact.phoneInfoTitle')}
            actionId="signup.contact-screen-why.toggle"
            data-testid="signup-contact-why"
          >
            {t('contact.phoneInfoBody')}
          </ExpandableCallout>
        )}
        {/* The one status announcer for this step: always mounted, its text
            changes. The visible error below is hidden from assistive
            technology so the message is not announced twice. */}
        <p className="signup-visually-hidden" aria-live="polite" data-testid="signup-status">
          {status}
        </p>
        <PrivateFields>
          {asking === 'email' ? (
            <TextInput
              label={t('contact.emailLabel')}
              type="email"
              inputMode="email"
              autoComplete="email"
              name="email"
              disabled={save === 'saving'}
              value={entered}
              errorText={fieldError ? t('contact.emailError') : undefined}
              onChangeText={onEdit}
              data-testid="signup-contact-email"
            />
          ) : (
            <PhoneInput
              actionId="signup.contact-screen-phone.change"
              label={t('contact.phoneLabel')}
              name="phone"
              disabled={save === 'saving'}
              value={entered}
              errorText={fieldError ? t('contact.phoneError') : undefined}
              onChangeText={onEdit}
              data-testid="signup-contact-phone"
            />
          )}
        </PrivateFields>
        {save === 'failed' ? (
          <div aria-hidden="true" data-testid="signup-save-error">
            <Callout tone="error" body={t('contact.saveError')} />
          </div>
        ) : null}
        <PrivacyNote text={t('contact.privacyNote')} />
        <FormActions>
          <Button
            size="L"
            type="Primary"
            label={t('actions.continue')}
            state={save === 'saving' ? 'Loading' : 'Default'}
            onPress={onContinue}
            actionId="signup.contact-screen-continue.activate"
            data-testid="signup-continue"
          />
        </FormActions>
      </div>
    </SignupShell>
  );
}

/** `/contact/`: the phone or email the member did not sign in with (v9 1.8 / 1.4-A.1). */
export function ContactStepScreen() {
  return (
    <EarlierAnswersGate step="contact">
      <ContactStep />
    </EarlierAnswersGate>
  );
}
